"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  Globe,
  Shield,
  Users,
  Activity,
  Bot,
  TrendingUp,
  AlertTriangle,
  Clock,
  Zap,
  Monitor,
  Smartphone,
  Edit,
  RefreshCw,
} from "lucide-react"
import { apiService } from "../lib/api-service"
import { CONFIG } from "../lib/config"

export function DashboardContent({ systemStats, liveVisitors, botActivity, campaignStats, config, setConfig }: any) {
  const [responseTimeMode, setResponseTimeMode] = useState<"average" | "full_analysis">("average")
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastUpdateTime, setLastUpdateTime] = useState<{ [key: string]: number }>({})
  const [apiErrors, setApiErrors] = useState<{ [key: string]: boolean }>({})
  
  // Use refs to store interval IDs
  const intervalsRef = useRef<NodeJS.Timeout[]>([])
  const abortControllerRef = useRef<AbortController | null>(null)

  // Ensure botActivity is always an array
  const safeBotActivity = Array.isArray(botActivity) ? botActivity : []
  const safeLiveVisitors = Array.isArray(liveVisitors) ? liveVisitors : []

  // Rate limiting helper
  const canMakeRequest = useCallback((endpoint: string, minInterval: number = 5000) => {
    const now = Date.now()
    const lastUpdate = lastUpdateTime[endpoint] || 0
    return (now - lastUpdate) >= minInterval
  }, [lastUpdateTime])

  // Update last request time
  const updateLastRequestTime = useCallback((endpoint: string) => {
    setLastUpdateTime(prev => ({ ...prev, [endpoint]: Date.now() }))
  }, [])

  // Enhanced API call with error handling and rate limiting
  const makeApiCall = useCallback(async (
    apiCall: () => Promise<any>, 
    endpoint: string, 
    minInterval: number = 5000
  ) => {
    // Check rate limiting
    if (!canMakeRequest(endpoint, minInterval)) {
      console.log(`Skipping ${endpoint} - rate limited`)
      return
    }

    // Check if there's an ongoing error for this endpoint
    if (apiErrors[endpoint]) {
      console.log(`Skipping ${endpoint} - previous error not resolved`)
      return
    }

    try {
      updateLastRequestTime(endpoint)
      
      // Create abort controller for this request
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // 10s timeout
      
      const response = await apiCall()
      clearTimeout(timeoutId)
      
      if (response.success) {
        console.log(`Updated ${endpoint} successfully`)
        // Clear any previous errors
        setApiErrors(prev => ({ ...prev, [endpoint]: false }))
      } else {
        throw new Error(`API call failed: ${response.error || 'Unknown error'}`)
      }
    } catch (error: any) {
      console.error(`Failed to update ${endpoint}:`, error.message)
      
      // Set error state to prevent immediate retries
      setApiErrors(prev => ({ ...prev, [endpoint]: true }))
      
      // Clear error state after a delay to allow retries
      setTimeout(() => {
        setApiErrors(prev => ({ ...prev, [endpoint]: false }))
      }, 30000) // Clear error after 30 seconds
    }
  }, [canMakeRequest, updateLastRequestTime, apiErrors])

  // Auto-refresh data based on intervals
  useEffect(() => {
    // Clear any existing intervals
    intervalsRef.current.forEach(interval => clearInterval(interval))
    intervalsRef.current = []

    // Real-time traffic monitoring with longer interval to prevent rate limiting
    const trafficInterval = setInterval(() => {
      makeApiCall(
        () => apiService.getLiveTraffic(),
        'live-traffic',
        10000 // Minimum 10 seconds between calls
      )
    }, Math.max(CONFIG.UPDATE_INTERVALS.REAL_TIME_TRAFFIC, 10000)) // At least 10 seconds

    intervalsRef.current.push(trafficInterval)

    // Dashboard stats with longer interval
    const statsInterval = setInterval(() => {
      makeApiCall(
        () => apiService.getDashboardStats(),
        'dashboard-stats',
        30000 // Minimum 30 seconds between calls
      )
    }, Math.max(CONFIG.UPDATE_INTERVALS.AGGREGATE_STATS, 60000)) // At least 60 seconds

    intervalsRef.current.push(statsInterval)

    // Bot detection with even longer interval
    const botInterval = setInterval(() => {
      makeApiCall(
        () => apiService.getBotDetection(),
        'bot-detection',
        45000 // Minimum 45 seconds between calls
      )
    }, 120000) // Every 2 minutes

    intervalsRef.current.push(botInterval)

    // Cleanup function
    return () => {
      intervalsRef.current.forEach(interval => clearInterval(interval))
      intervalsRef.current = []
      
      // Abort any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [makeApiCall])

  const handleRefresh = async () => {
    if (isRefreshing) return // Prevent multiple simultaneous refreshes
    
    setIsRefreshing(true)
    
    try {
      // Create new abort controller for refresh
      abortControllerRef.current = new AbortController()
      
      // Sequential API calls to prevent overwhelming the server
      await makeApiCall(() => apiService.getDashboardStats(), 'dashboard-stats', 0)
      
      // Wait a bit between calls
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      await makeApiCall(() => apiService.getLiveTraffic(), 'live-traffic', 0)
      
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      await makeApiCall(() => apiService.getBotDetection(), 'bot-detection', 0)
      
    } catch (error) {
      console.error("Refresh failed:", error)
    } finally {
      setIsRefreshing(false)
      abortControllerRef.current = null
    }
  }

  const getResponseTimeDisplay = () => {
    if (responseTimeMode === "average") {
      return {
        value: `${systemStats.avgResponseTime}ms`,
        label: "Average Page Load",
        description: "Average time for visitors to load pages",
      }
    } else {
      return {
        value: `${systemStats.avgResponseTime + 45}ms`,
        label: "Full Analysis Time",
        description: "Average time for complete security analysis",
      }
    }
  }

  const responseTimeData = getResponseTimeDisplay()

  return (
    <>
      {/* Quick Actions */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-100 flex items-center">
              <Activity className="mr-2 h-5 w-5 text-cyan-500" />
              System Overview
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="bg-transparent border-slate-600 hover:bg-slate-800"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? "animate-spin" : ""}`} />
                {isRefreshing ? "Refreshing..." : "Refresh"}
              </Button>
              {/* Connection status indicator */}
              <div className="flex items-center space-x-1">
                <div className={`w-2 h-2 rounded-full ${
                  Object.values(apiErrors).some(Boolean) ? 'bg-red-400' : 'bg-green-400'
                }`} />
                <span className="text-xs text-slate-400">
                  {Object.values(apiErrors).some(Boolean) ? 'Connection Issues' : 'Connected'}
                </span>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Total Requests */}
        <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm hover:bg-slate-900/70 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm mb-1">Total Requests</p>
                <p className="text-3xl font-bold text-cyan-400">{systemStats.totalRequests?.toLocaleString()}</p>
                <p className="text-slate-500 text-xs mt-1">{systemStats.requestsPerSecond}/sec</p>
              </div>
              <div className="relative">
                <Globe className="h-8 w-8 text-cyan-500" />
                <TrendingUp className="h-4 w-4 text-green-400 absolute -bottom-1 -right-1" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bot Blocked */}
        <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm hover:bg-slate-900/70 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm mb-1">Bot Blocked</p>
                <p className="text-3xl font-bold text-red-400">{systemStats.botPercentage}%</p>
                <p className="text-slate-500 text-xs mt-1">{systemStats.botBlocked?.toLocaleString()} blocked</p>
              </div>
              <div className="relative">
                <Shield className="h-8 w-8 text-red-500" />
                <Bot className="h-4 w-4 text-red-400 absolute -bottom-1 -right-1" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Visitors */}
        <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm hover:bg-slate-900/70 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm mb-1">Active Visitors</p>
                <p className="text-3xl font-bold text-green-400">{systemStats.activeVisitors}</p>
                <p className="text-slate-500 text-xs mt-1">{systemStats.uniqueVisitors} unique</p>
              </div>
              <div className="relative">
                <Users className="h-8 w-8 text-green-500" />
                <TrendingUp className="h-4 w-4 text-green-400 absolute -bottom-1 -right-1" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Response Time with Mode Selector */}
        <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm hover:bg-slate-900/70 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-slate-400 text-sm">Response Time</p>
                  <Select value={responseTimeMode} onValueChange={setResponseTimeMode}>
                    <SelectTrigger className="w-auto h-6 text-xs bg-slate-800 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="average">Average</SelectItem>
                      <SelectItem value="full_analysis">Full Analysis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-3xl font-bold text-blue-400">{responseTimeData.value}</p>
                <p className="text-slate-500 text-xs mt-1">{responseTimeData.description}</p>
              </div>
              <div className="relative">
                <Clock className="h-8 w-8 text-blue-500" />
                <Activity className="h-4 w-4 text-blue-400 absolute -bottom-1 -right-1" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm mb-6">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center">
            <Zap className="mr-2 h-5 w-5 text-yellow-500" />
            System Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Overall Health</span>
                <span className="text-green-400 font-bold">{systemStats.systemHealth}%</span>
              </div>
              <Progress value={systemStats.systemHealth} className="h-2">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full"
                  style={{ width: `${systemStats.systemHealth}%` }}
                />
              </Progress>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Security Level</span>
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                  {config.securityMode?.toUpperCase()}
                </Badge>
              </div>
              <Progress value={75} className="h-2">
                <div
                  className="h-full bg-gradient-to-r from-yellow-500 to-orange-400 rounded-full"
                  style={{ width: "75%" }}
                />
              </Progress>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Performance</span>
                <span className="text-blue-400 font-bold">92%</span>
              </div>
              <Progress value={92} className="h-2">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full"
                  style={{ width: "92%" }}
                />
              </Progress>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Uptime</span>
                <span className="text-purple-400 font-bold">99.9%</span>
              </div>
              <Progress value={99.9} className="h-2">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-400 rounded-full"
                  style={{ width: "99.9%" }}
                />
              </Progress>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Live Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Recent Visitors */}
        <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-slate-100 flex items-center">
              <Users className="mr-2 h-5 w-5 text-green-500" />
              Recent Visitors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {safeLiveVisitors.slice(0, 5).map((visitor: any) => (
                <div key={visitor.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      {visitor.device === "Mobile" ? (
                        <Smartphone className="h-4 w-4 text-slate-400" />
                      ) : (
                        <Monitor className="h-4 w-4 text-slate-400" />
                      )}
                      <span className="text-sm font-mono text-slate-300">{visitor.ip}</span>
                    </div>
                    <Badge className="text-xs">{visitor.country}</Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge
                      className={`text-xs ${
                        visitor.status === "legitimate"
                          ? "bg-green-500/20 text-green-400 border-green-500/50"
                          : "bg-red-500/20 text-red-400 border-red-500/50"
                      }`}
                    >
                      {visitor.status}
                    </Badge>
                    <span className="text-xs text-slate-500">{new Date(visitor.timestamp).toLocaleTimeString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Bot Activity */}
        <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-slate-100 flex items-center">
              <Bot className="mr-2 h-5 w-5 text-red-500" />
              Bot Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {safeBotActivity.slice(0, 5).map((activity: any) => (
                <div key={activity.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <AlertTriangle className="h-4 w-4 text-red-400" />
                    <div>
                      <div className="text-sm font-medium text-slate-200">{activity.pattern}</div>
                      <div className="text-xs text-slate-500 font-mono">{activity.ip}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge
                      className={`text-xs ${
                        activity.action === "blocked"
                          ? "bg-red-500/20 text-red-400 border-red-500/50"
                          : "bg-yellow-500/20 text-yellow-400 border-yellow-500/50"
                      }`}
                    >
                      {activity.action}
                    </Badge>
                    <span className="text-xs text-slate-500">{new Date(activity.timestamp).toLocaleTimeString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaign Performance */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center">
            <TrendingUp className="mr-2 h-5 w-5 text-purple-500" />
            Campaign Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Campaign A */}
            <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/30">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-slate-200">Campaign A</h3>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <Edit className="h-3 w-3" />
                </Button>
              </div>

              <div className="mb-3">
                <div className="text-xs text-slate-500 mb-1">Campaign URL</div>
                <div className="text-xs font-mono text-slate-300 bg-slate-800/50 rounded px-2 py-1 truncate">
                  {campaignStats.campaignA.url}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 text-xs mb-3">
                <div>
                  <div className="text-slate-500">Traffic</div>
                  <div className="text-lg font-bold text-blue-400">{campaignStats.campaignA.traffic}%</div>
                </div>
                <div>
                  <div className="text-slate-500">Conversions</div>
                  <div className="text-lg font-bold text-green-400">{campaignStats.campaignA.conversions}</div>
                </div>
                <div>
                  <div className="text-slate-500">Bounce Rate</div>
                  <div className="text-lg font-bold text-red-400">{campaignStats.campaignA.bounceRate}%</div>
                </div>
              </div>

              <Progress value={campaignStats.campaignA.traffic} className="h-2">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
                  style={{ width: `${campaignStats.campaignA.traffic}%` }}
                />
              </Progress>
            </div>

            {/* Campaign B */}
            <div className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/30">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-slate-200">Campaign B</h3>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <Edit className="h-3 w-3" />
                </Button>
              </div>

              <div className="mb-3">
                <div className="text-xs text-slate-500 mb-1">Campaign URL</div>
                <div className="text-xs font-mono text-slate-300 bg-slate-800/50 rounded px-2 py-1 truncate">
                  {campaignStats.campaignB.url}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 text-xs mb-3">
                <div>
                  <div className="text-slate-500">Traffic</div>
                  <div className="text-lg font-bold text-purple-400">{campaignStats.campaignB.traffic}%</div>
                </div>
                <div>
                  <div className="text-slate-500">Conversions</div>
                  <div className="text-lg font-bold text-green-400">{campaignStats.campaignB.conversions}</div>
                </div>
                <div>
                  <div className="text-slate-500">Bounce Rate</div>
                  <div className="text-lg font-bold text-red-400">{campaignStats.campaignB.bounceRate}%</div>
                </div>
              </div>

              <Progress value={campaignStats.campaignB.traffic} className="h-2">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
                  style={{ width: `${campaignStats.campaignB.traffic}%` }}
                />
              </Progress>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  )
}
